import axios, { AxiosResponse } from "axios";
import { adaAPI } from "services/api";
import {
  checkUserExpiry,
  resetSessionTimeout,
  setSessionTimeoutModal
} from "actions";
import { setErrorPage } from "actions/error";
import { goToPage, setPage } from "actions/router";
import { State } from "reducers/types";
import { CallApiAction, Dispatch } from "actions/types";

function statusCodeToStringCode(returnedStatusCode: number): string {
  if (!returnedStatusCode) {
    return "error";
  }

  return returnedStatusCode.toString()[0] === "2" ? "success" : "error";
}

interface Store {
  dispatch: Dispatch;
  getState: () => State;
}

export default (store: Store) => (next: <T>(action: Record<string, unknown>) => T) => (
  action: CallApiAction
) => {
  if (!action) {
    return null;
  }

  if (action instanceof Promise) {
    return null;
  }

  // So the middleware doesn't get applied to every single action
  if (typeof action.CALL_API === "undefined") {
    return next(action as unknown as Record<string, unknown>);
  }

  const callAPI = action.CALL_API;

  const {
    method,
    endpoint,
    external,
    payload,
    types,
    args,
    params,
    dispatchCallbacks,
    cancelPrevious,
    responseType
  } = callAPI;
  const [
    requestType = null,
    successType = null,
    errorType = null,
    abortedType = null
  ] = types || [];

  // Make a clone of payload to be used by request actions
  const payloadClone = { ...payload };

  // Generate REQUEST action-type
  if (requestType) {
    next(
      {
        ...args,
        requestPayload: payloadClone,
        type: requestType
      }
    );
  }

  // Check password expiry status on each call
  store.dispatch(checkUserExpiry());

  // Cancel previous request of same type if specified
  if (cancelPrevious) {
    adaAPI.cancelLastRequest(requestType);
  }

  return adaAPI.request({
    method,
    url: endpoint,
    params,
    data: payload,
    requestType,
    responseType
  })
    .then(
      (response): (
      ({response: AxiosResponse<Record<string, unknown>>} | {type: typeof successType})) => {
        store.dispatch(resetSessionTimeout());

        if (!successType) {
          return {
            response
          };
        }

        return next({ response, ...args, type: successType });
      },
      (error) => {
        if (axios.isCancel(error)) {
          console.warn("Request aborted");

          return next({
            response: {
              status: 499,
              statusText: "CLIENT CLOSED REQUEST"
            },
            type: abortedType
          });
        }

        const errorResponse = error.response;
        const includes401 = dispatchCallbacks &&
          dispatchCallbacks.some(dispatchCallback => dispatchCallback.fireOnStatus === 401);

        // Handling the case when there's no internet connection (no response)
        if (!errorResponse) {
          if (!errorType) {
            return { response: { status: "error" } };
          }

          return next({
            response: {
              status: null
            },
            ...args,
            type: errorType
          });
        }

        if (errorResponse.status === 401 && !includes401 && !external && !(window.location.pathname === "/")) {
          if (errorResponse.data.reason === "mfa_not_setup") {
            store.dispatch({
              type: "SET_MODAL",
              payload: {
                isOpen: true,
                view: "MODAL_ENFORCE_MFA",
                modalProps: {}
              }
            });
          } else {
            // Auto logout user if unauthorized status code
            store.dispatch({
              type: "CLEAR_CREDENTIALS"
            });

            store.dispatch({
              type: "UNAUTHENTICATE"
            });

            store.dispatch(goToPage("/"));
          }
        } else if (errorResponse.status === 403) {
          // Token invalid
          store.dispatch({
            type: "CLEAR_CREDENTIALS"
          });

          store.dispatch({
            type: "UNAUTHENTICATE"
          });

          // if we're not already on the root page, take us home
          if (!(window.location.pathname === "/")) {
            store.dispatch(setPage("/"));
          }
        } else if (errorResponse.status === 419 && store.getState().session.isAuthenticated) {
          // Session has timed out - if already unauthenticated, no need to set modal
          store.dispatch(setSessionTimeoutModal());
        } else if (errorResponse.status === 503) {
          store.dispatch(setErrorPage(true, errorResponse.status));
        }

        if (errorType) {
          return next({
            response: errorResponse,
            ...args,
            type: errorType
          });
        }

        return {
          response: errorResponse
        };
      }
    )
    .then((newAction) => {
      // Dispatch other actions on completion
      if (newAction && ("response" in newAction) && dispatchCallbacks) {
        dispatchCallbacks.forEach((dispatchCallback) => {
          if (
            newAction.response.status === dispatchCallback.fireOnStatus ||
            statusCodeToStringCode(newAction.response.status) === dispatchCallback.fireOnStatus
          ) {
            const newArgs = {
              ...newAction,
              ...(dispatchCallback.args ? dispatchCallback.args : {})
            };
            store.dispatch(dispatchCallback.request(newArgs));
          }
        });
      }

      // TODO: Find a better solution; we sometimes need this promise to reject
      if (("type" in newAction) && newAction.type.indexOf("VARIABLE_FAILURE") !== -1) {
        return Promise.reject(newAction);
      }

      return undefined;
    });
};
